// Daytime map layers: coffee / eat / beer / shops / art — directory businesses bright with logo, OpenStreetMap fill-ins dimmed as "not yet verified".
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import axios from "axios";
import { ExternalLink, BadgeCheck } from "lucide-react";
import { DirectionsActions } from "./DirectionsActions";
import { openStatus } from "../utils/openNow";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const absUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export const DAY_KINDS = {
  coffee: { label: "Coffee & tea", emoji: "☕", color: "#8B5A2B" },
  eat: { label: "Eat", emoji: "🍽️", color: "#E76F51" },
  beer: { label: "Beer & cider", emoji: "🍺", color: "#D4A017" },
  shop: { label: "Shops & thrift", emoji: "🛍️", color: "#1d4ed8" },
  art: { label: "Art & sober-friendly", emoji: "🎨", color: "#16A085" },
};

const cache = {};
export const placeIcon = (kind, logoUrl, verified, status) => {
  const tag = status ? `${status.status}:${status.short}` : "";
  const key = `${kind}-${logoUrl || ""}-${verified ? 1 : 0}-${tag}`;
  if (cache[key]) return cache[key];
  const m = DAY_KINDS[kind] || DAY_KINDS.shop;
  const size = logoUrl ? 40 : verified ? 32 : 26;
  const badge = status
    ? `<span data-testid="day-place-hours-badge" style="position:absolute;left:50%;top:${size + 2}px;transform:translateX(-50%);white-space:nowrap;font:700 9px/14px -apple-system,sans-serif;padding:0 5px;border-radius:7px;color:#fff;background:${status.status === "open" ? "#15803d" : "#6b7280"};box-shadow:0 1px 3px rgba(0,0,0,.3)">${status.status === "open" ? "● " : ""}${status.short}</span>`
    : "";
  const inner = logoUrl
    ? `<div style="transform:rotate(45deg);width:${size - 8}px;height:${size - 8}px;border-radius:50%;background:#fff;overflow:hidden"><img src="${absUrl(logoUrl)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block" loading="lazy"/></div>`
    : `<span style="transform:rotate(45deg);font-size:${verified ? 15 : 12}px;line-height:1">${m.emoji}</span>`;
  cache[key] = L.divIcon({
    className: `day-place-marker${verified ? "" : " day-place-dim"}`,
    html: `<div style="position:relative;width:${size}px;height:${size}px"><div style="width:${size}px;height:${size}px;background:${logoUrl ? "#fff" : m.color};border-radius:50% 50% 50% 0;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid ${verified ? "#fff" : "rgba(255,255,255,0.85)"};box-shadow:0 2px 8px rgba(0,0,0,${verified ? 0.3 : 0.18});opacity:${verified ? 1 : 0.62}">${inner}</div>${badge}</div>`,
    iconSize: [size, size], iconAnchor: [size / 2, size], popupAnchor: [0, -size],
  });
  return cache[key];
};

export const useDayPlaces = (on) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!on || data) return;
    axios.get(`${API}/places`).then((r) => setData(r.data)).catch(() => setData({ places: [] }));
  }, [on]); // eslint-disable-line react-hooks/exhaustive-deps
  return data;
};

export const DayLayerChips = ({ sel, setSel, count, loading }) => {
  const toggle = (k) => setSel((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));
  const all = Object.keys(DAY_KINDS);
  const allOn = sel.length === all.length;
  return (
    <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="day-layer-chips">
      <span className="text-[10px] font-bold uppercase tracking-widest text-[#5C5C5C] mr-0.5">Show:</span>
      <button type="button" onClick={() => setSel(allOn ? [] : all)} aria-pressed={allOn} data-testid="day-layer-all" data-insight="Day map · Show all places"
        className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors border ${allOn ? "bg-[#1A1A1A] border-[#1A1A1A] text-white" : "bg-white border-[#E2E0DC] text-[#5C5C5C] hover:border-[#1A1A1A]"}`}>
        Show all
      </button>
      {Object.entries(DAY_KINDS).map(([k, m]) => {
        const on = sel.includes(k);
        return (
          <button key={k} type="button" onClick={() => toggle(k)} aria-pressed={on} data-testid={`day-layer-${k}`} data-insight={`Day map · ${m.label}`}
            className="px-3 py-1.5 rounded-full text-xs font-bold transition-colors border"
            style={on ? { background: m.color, borderColor: m.color, color: "#fff" } : { background: "#fff", borderColor: "#E2E0DC", color: "#5C5C5C" }}>
            {m.emoji} {m.label}
          </button>
        );
      })}
      {sel.length > 0 && (
        <>
          <span className="text-xs text-[#5C5C5C]" data-testid="day-layer-count">{loading ? "Loading places…" : `${count} place${count === 1 ? "" : "s"} · dim pins are from OpenStreetMap, not yet verified`}</span>
          <button type="button" onClick={() => setSel([])} className="px-3 py-1.5 rounded-full text-xs font-bold bg-[#1A1A1A] text-white hover:bg-[#2E5C55]" data-testid="day-layer-clear">Events only</button>
        </>
      )}
    </div>
  );
};

export const DayPlaceMarkers = ({ data, sel }) => {
  const list = useMemo(() => (data?.places || []).filter((p) => p.kinds.some((k) => sel.includes(k))), [data, sel]);
  const refs = useRef({});
  const map = useMap();
  const pending = useRef(null);
  // "wall:open-business-pin" (from the Gem Hoard): fly to the business and open its popup once its marker exists.
  useEffect(() => {
    const go = () => { const id = pending.current; const m = id && refs.current[id]; if (m) { map.flyTo(m.getLatLng(), Math.max(map.getZoom(), 14), { duration: 0.8 }); setTimeout(() => m.openPopup(), 850); pending.current = null; } };
    const h = (e) => { pending.current = e.detail?.business_id; go(); };
    window.addEventListener("wall:open-business-pin", h);
    if (window.__wallPendingPin) { pending.current = window.__wallPendingPin; window.__wallPendingPin = null; }  // event fired before the layer mounted
    go();
    return () => window.removeEventListener("wall:open-business-pin", h);
  }, [list, map]);
  return list.map((p) => {
    const m = DAY_KINDS[p.kind] || DAY_KINDS.shop;
    const st = openStatus(p.hours);
    const site = p.website ? (p.website.startsWith("http") ? p.website : `https://${p.website}`) : "";
    return (
      <Marker key={p.id} position={[p.lat, p.lng]} icon={placeIcon(p.kind, p.logo_url, p.verified, st)} zIndexOffset={p.verified ? 500 : -200} ref={(m) => { if (m) refs.current[p.id] = m; }}>
        <Popup maxWidth={280} minWidth={210}>
          <div className="min-w-[200px]" data-testid={`day-place-popup-${p.id.replace("/", "-")}`}>
            <div className="flex items-start gap-2">
              {p.logo_url && <img src={absUrl(p.logo_url)} alt="" className="h-10 w-10 rounded-full object-cover border border-[#E2E0DC] shrink-0" />}
              <div className="min-w-0">
                <div className="font-bold text-[14px] text-[#1A1A1A] leading-tight">{p.name}</div>
                <div className="text-[11px] font-semibold mt-0.5" style={{ color: m.color }}>{m.emoji} {m.label}{p.cuisine ? ` · ${p.cuisine.replace(/_/g, " ")}` : ""}{p.sober_friendly ? " · 🌿 sober-friendly" : ""}</div>
              </div>
            </div>
            {p.tagline && <p className="m-0 mt-1 text-[12px] text-[#5C5C5C] italic">{p.tagline}</p>}
            {p.address && <p className="m-0 mt-1 text-[11px] text-[#5C5C5C]">{p.address}</p>}
            {st && <p className={`m-0 mt-1 text-[11px] font-bold ${st.status === "open" ? "text-[#15803d]" : "text-[#6b7280]"}`} data-testid="day-place-open-status">{st.status === "open" ? "● " : "○ "}{st.label}</p>}
            {p.hours && <p className="m-0 mt-0.5 text-[11px] text-[#5C5C5C]">Hours: {p.hours}{st ? "" : " — couldn't read these, check before you go"}</p>}
            {!p.hours && <p className="m-0 mt-0.5 text-[11px] text-[#9CA3AF]">Hours not posted — check before you go</p>}
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {p.verified
                ? <span className="inline-flex items-center gap-1 rounded-full bg-[#2E5C55]/10 text-[#2E5C55] px-2 py-0.5 text-[10px] font-bold" data-testid="day-place-verified"><BadgeCheck className="h-3 w-3" /> On THE WALL</span>
                : <span className="rounded-full bg-[#F5F5F5] text-[#5C5C5C] px-2 py-0.5 text-[10px] font-bold" data-testid="day-place-unverified">From OpenStreetMap · not yet verified</span>}
              {site && <a href={site} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] font-bold text-[#1d4ed8]">Website <ExternalLink className="h-3 w-3" /></a>}
            </div>
            <div className="border-t border-[#E2E0DC] mt-2 pt-2">
              <DirectionsActions variant="compact" size="xs" lat={p.lat} lng={p.lng} address={p.address || ""} testId={`day-place-directions-${p.id.replace("/", "-")}`} />
            </div>
            {p.verified
              ? (p.feature_page || p.slug) && <Link to={p.feature_page || `/businesses/${p.slug}`} className="mt-2 block text-center text-[11px] font-bold text-[#2E5C55] underline" data-testid="day-place-page-link">See their page on THE WALL →</Link>
              : <Link to="/for-businesses" className="mt-2 block text-center text-[11px] font-bold text-[#2E5C55] underline" data-testid="day-place-claim-link">Own this place? Put it on THE WALL — free →</Link>}
          </div>
        </Popup>
      </Marker>
    );
  });
};
